import { InjectionToken } from '@angular/core';

/**
 * Reloads the app. With a `url` it hard-navigates to that destination (used by
 * the version check on `NavigationStart`, so the user lands on the page they
 * actually clicked — running on fresh code); without one it reloads the current
 * page (used by the chunk-error recovery).
 */
export type BdReloadPage = (url?: string) => void;

export const BD_RELOAD_PAGE = new InjectionToken<BdReloadPage>(
  'BD_RELOAD_PAGE',
  {
    providedIn: 'root',
    factory: () => (url?: string) => {
      if (url) {
        window.location.assign(url);
      } else {
        window.location.reload();
      }
    },
  },
);

/**
 * Result of a {@link reloadIfAllowed} call, so callers can emit telemetry:
 * - `reloaded`   — a reload was performed,
 * - `debounced`  — skipped because one just happened (or no browser context),
 * - `suppressed` — skipped because the per-window cap was hit (likely a broken
 *   deploy stuck in a reload loop — the signal worth alerting on).
 */
export type ReloadOutcome = 'reloaded' | 'debounced' | 'suppressed';

const RELOAD_LOG_KEY = 'bd_reload_log';
// Coalesce a burst of reload requests (e.g. several lazy chunks failing at
// once, or an immediate re-error right after a reload) into a single reload.
const RELOAD_DEBOUNCE_MS = 10_000;
// Sliding window over which reloads are counted for the loop cap.
const RELOAD_WINDOW_MS = 60_000;
// After this many reloads inside the window, stop reloading and let the
// underlying error surface instead of refreshing forever on a broken deploy.
const MAX_RELOADS_PER_WINDOW = 3;

function readRecentReloads(now: number): number[] {
  try {
    const raw = sessionStorage.getItem(RELOAD_LOG_KEY);
    const parsed: unknown = raw ? JSON.parse(raw) : [];
    if (!Array.isArray(parsed)) {
      return [];
    }
    return parsed
      .filter((ts): ts is number => typeof ts === 'number')
      .filter((ts) => now - ts < RELOAD_WINDOW_MS);
  } catch {
    return [];
  }
}

/**
 * Shared across BdVersionCheckService and BdUserErrorHandler so a chunk-error
 * reload and a version-update reload cannot ping-pong inside the same session.
 *
 * Guards against two failure modes:
 * - bursts/tight loops are debounced (one reload per {@link RELOAD_DEBOUNCE_MS}),
 * - a genuinely broken deploy is capped at {@link MAX_RELOADS_PER_WINDOW}
 *   reloads per window, after which the error is allowed to surface.
 *
 * Legitimate reloads (one per deployment, minutes apart) never accumulate in
 * the window and are therefore always allowed.
 */
export function reloadIfAllowed(
  reload: BdReloadPage,
  url?: string,
): ReloadOutcome {
  // Non-browser context (e.g. SSR) — no sessionStorage and nothing to reload.
  if (typeof sessionStorage === 'undefined') {
    return 'debounced';
  }

  const now = Date.now();
  const recent = readRecentReloads(now);

  const last = recent[recent.length - 1];
  if (last !== undefined && now - last < RELOAD_DEBOUNCE_MS) {
    return 'debounced';
  }
  if (recent.length >= MAX_RELOADS_PER_WINDOW) {
    return 'suppressed';
  }

  recent.push(now);
  try {
    sessionStorage.setItem(RELOAD_LOG_KEY, JSON.stringify(recent));
  } catch {
    // sessionStorage unavailable (e.g. privacy mode); reload anyway as a
    // best effort — we just lose loop protection in that rare case.
  }
  reload(url);
  return 'reloaded';
}
